/**
 * Mapa adres Codablie projektu — pro prompt i UI.
 *
 * Projekt má troje adresy a ve všech třech se říká „náhled":
 *  - **DEV náhled** — vite dev server na workspace instanci
 *    (`<slug>.cdb.services.maxcloud.cz`); změny workspaceu jsou tam hned,
 *    bez commitu;
 *  - **ostrá adresa** — veřejnost, obsluhuje ji release z karuselu;
 *  - **náhled ostré** — modrá doména PROD instance (`apphost.preview_set`),
 *    kandidát na ostrou. Ve výstupech `apphost.release_switch` se jí říká
 *    `preview`, proto ty dva náhledy splývají.
 *
 * Filip 2026-09-10: „agent nechápe, kde je náhledová adresa v systému
 * karuselu pojmenovaný preview". Dřív prompt nesl jediný řádek
 * `**Preview:** <dev adresa>` — tedy přesně to jméno, pod kterým se v
 * karuselu skrývá něco jiného. Mapa rozlišuje jména a mlčí-li o adrese,
 * říká proč: nevymyšlená adresa je užitečnější než vymyšlená, protože
 * vymyšlenou agent oznámí jako fakt.
 */

export interface CodableAddressInput {
  slug: string;
  /** Živý DEV workspace (vite), typicky `https://<slug>.cdb.services…`. */
  devPreviewUrl?: string | null;
  /** Ostrá adresa = to, co vidí veřejnost (`publish.url` / `production.ui_url`). */
  prodUrl?: string | null;
  /** Modrá náhledová adresa PROD instance (`production.preview_url`). */
  prodPreviewUrl?: string | null;
  /** Stav karuselu release (Ostrá / Náhled / Záloha). */
  carousel?: {
    live?: string | null;
    preview?: string | null;
    reserved?: string | null;
    preview_is_live?: boolean;
  } | null;
}

/** Očekávaná dev doména — platí jen jako stopa, proto se značí jako neověřená. */
export function expectedDevPreviewUrl(slug: string): string {
  return `https://${slug}.cdb.services.maxcloud.cz`;
}

/**
 * Řádky do orientačního bloku promptu. Prázdné hodnoty se nezamlčují ani
 * nedoplňují výmyslem — řádek existuje a říká, že hodnota není známa.
 */
export function codableAddressLines(input: CodableAddressInput): string[] {
  const { slug, devPreviewUrl, prodUrl, prodPreviewUrl, carousel } = input;
  const lines: string[] = ['### Adresy — troje, a ve všech se říká „náhled"'];

  lines.push(
    devPreviewUrl
      ? `- **DEV náhled** (živej workspace, změny hned bez commitu): \`${devPreviewUrl}\``
      : `- **DEV náhled** nezjištěný; očekávaná adresa \`${
        expectedDevPreviewUrl(slug)
      }\` — **neověřeno**, dřív než ji někomu sdělíš, ověř ji (\`codable_project_get\`)`,
  );

  lines.push(
    prodUrl
      ? `- **Ostrá adresa** (veřejnost, obsluhuje ji release z karuselu): \`${prodUrl}\``
      : '- **Ostrá adresa** zatím nepublikovaná — veřejnost nic nevidí, `publish.actual` je vypnutý',
  );

  lines.push(
    prodPreviewUrl
      ? `- **Náhled ostré** (modrá doména PROD instance): \`${prodPreviewUrl}\` — kandidát na ostrou. V karuselu se jí říká „preview", ale **není to DEV náhled**.`
      : '- **Náhled ostré** (modrá doména PROD instance) není nastavený (`apphost.preview_set`). V karuselu se „náhled" jmenuje táž věc — DEV náhled to pořád není',
  );

  if (carousel && (carousel.live || carousel.preview || carousel.reserved)) {
    const parts = [
      `Ostrá=${carousel.live ?? '—'}`,
      `Náhled=${carousel.preview ?? '—'}`,
      `Záloha=${carousel.reserved ?? '—'}`,
    ].join(' · ');
    lines.push(
      carousel.preview_is_live
        ? `- Karusel release: ${parts} — náhled ukazuje totéž co ostrá, není co přepínat`
        : `- Karusel release: ${parts} — přepnutí mění, která release obslučuje ostrou adresu`,
    );
  }

  lines.push(
    '- Adresu, kterou tady nemáš, nevymýšlej: ověř si ji `codable_project_get`, nebo řekni, že ji neznáš.',
  );
  return lines;
}
